'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import { analytics } from '@/lib/analytics/posthog'
import { z } from '@/lib/validation/zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useId, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { formatMessage } from '@/components/site/forms/form-helpers'
import { ReCaptcha } from '@/components/site/forms/register/recaptcha'
import { Button } from '@/components/ui/button'
import { ErrorMessage, Field, Label } from '@/components/ui/fieldset'
import { Link } from '@/components/ui/link'
import { WorkspaceInput } from '@/components/ui/workspace-input'
import { recaptchaSiteKey } from '@/config/env.public'
import type { Dictionary } from '@/i18n/dictionaries/base'
import { ArrowRightIcon } from '@heroicons/react/24/outline'

type LoginFormProps = {
  ui: Dictionary['ui']
  copy: Dictionary['pages']['login']
  workspaceSuffix: string
  signupHref: string
  /** Passed to reCAPTCHA so it renders in the visitor's language. */
  locale: string
}

const LIMITS = {
  workspaceMin: 4,
  workspaceMax: 32,
} as const

export function LoginForm({ ui, copy, workspaceSuffix, signupHref, locale }: LoginFormProps) {
  const workspaceId = useId()
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const [captchaError, setCaptchaError] = useState<string | null>(null)
  /**
   * A reCAPTCHA token is single-use. Every submit spends the one in hand, so the widget has to be
   * reset afterwards or a second attempt — a typo in the workspace, which is the common case here —
   * would send a token the backend has already seen and get refused for the wrong reason.
   */
  const [captchaResetKey, setCaptchaResetKey] = useState(0)
  const schema = useMemo(
    () =>
      z
        .object({
          workspace: z
            .string()
            .trim()
            .min(1, ui.validation.workspaceRequired)
            .min(LIMITS.workspaceMin, formatMessage(ui.validation.workspaceMin, { min: LIMITS.workspaceMin }))
            .max(LIMITS.workspaceMax, formatMessage(ui.validation.workspaceMax, { max: LIMITS.workspaceMax }))
            .regex(/^[a-z-]+$/, ui.validation.workspacePattern)
            .refine((value) => !value.startsWith('-') && !value.endsWith('-'), ui.validation.workspacePattern),
        })
        .strict(),
    [
      ui.validation.workspaceRequired,
      ui.validation.workspaceMin,
      ui.validation.workspaceMax,
      ui.validation.workspacePattern,
    ],
  )

  const {
    register,
    handleSubmit,
    setError,
    setFocus,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<{ workspace: string }>({
    resolver: zodResolver(schema),
    defaultValues: {
      workspace: '',
    },
  })

  const buildWorkspaceUrl = (workspace: string) => {
    const normalizedSuffix = workspaceSuffix.startsWith('.') ? workspaceSuffix.slice(1) : workspaceSuffix
    const hostname = `${workspace}.${normalizedSuffix}`
    const isLocal = normalizedSuffix.endsWith('.test') || normalizedSuffix.includes('localhost')
    const protocol = isLocal ? 'http' : 'https'
    return `${protocol}://${hostname}/login`
  }

  /**
   * Resetting a password happens inside the firm's own environment, and which environment that is
   * only becomes known once the workspace is typed. So the link asks for it rather than guessing:
   * with a workspace it opens the reset page there, without one it puts the cursor in the field.
   */
  const onForgotPassword = () => {
    const workspace = getValues('workspace').trim()
    if (!schema.safeParse({ workspace }).success) {
      setError('workspace', { message: copy.form.forgotPasswordNeedsWorkspace })
      setFocus('workspace')
      return
    }
    window.open(buildWorkspaceUrl(workspace).replace(/\/login$/, '/forgot-password'), '_blank', 'noopener,noreferrer')
  }

  const onSubmit = handleSubmit(async ({ workspace }) => {
    if (recaptchaSiteKey && !captchaToken) {
      setCaptchaError(ui.validation.captchaRequired)
      return
    }

    try {
      const response = await fetch('/api/exists', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: workspace, token: captchaToken }),
      })

      if (response.status === 204) {
        // Returning user with an existing workspace heading into the app.
        analytics.capture('workspace_login_requested', { workspace })
        const targetUrl = buildWorkspaceUrl(workspace)
        window.open(targetUrl, '_blank', 'noopener,noreferrer')
        return
      }

      if (response.status === 429) {
        setError('workspace', { message: ui.validation.tooManyRequests })
        return
      }

      const contentType = response.headers.get('content-type') ?? ''
      const isJson = contentType.includes('application/json')
      const data = isJson ? await response.json().catch(() => null) : null
      const errorBag = (data as { errors?: Record<string, unknown> } | null)?.errors

      /**
       * A rejected captcha comes back as a validation error keyed on `token` (missing) or on the
       * backend's own `message`/`error` pair (present but refused or expired). Either way the fix
       * is the same one the visitor can act on: solve it again. Pointing that at the workspace
       * field instead would blame the one thing that was probably right.
       */
      if (response.status === 422 && errorBag && ('token' in errorBag || 'error' in errorBag)) {
        setCaptchaError(ui.validation.captchaRequired)
        return
      }

      if (response.status !== 404 && response.status !== 422) {
        setError('workspace', { message: ui.validation.submitFailed })
        return
      }

      const domainError = errorBag?.domain
      const workspaceError = errorBag?.workspace
      const fieldErrorSource = typeof domainError !== 'undefined' ? domainError : workspaceError
      const fieldError = Array.isArray(fieldErrorSource) ? fieldErrorSource[0] : fieldErrorSource

      if (typeof fieldError === 'string') {
        setError('workspace', { message: fieldError })
        return
      }

      if (typeof data?.message === 'string') {
        setError('workspace', { message: data.message })
        return
      }

      setError('workspace', { message: ui.validation.workspaceNotFound })
    } catch {
      setError('workspace', { message: ui.validation.submitFailed })
    } finally {
      // Spent either way — including on success, because the visitor stays on this page while the
      // workspace opens in a new tab and may well type a second one.
      setCaptchaToken(null)
      setCaptchaResetKey((key) => key + 1)
    }
  })

  return (
    <form className="mt-6 space-y-5" action="#" method="post" noValidate onSubmit={onSubmit}>
      <Field>
        <Label htmlFor={workspaceId} className="text-mist-900 dark:text-mist-100" required>
          {ui.fields.workspace}
        </Label>
        <WorkspaceInput
          id={workspaceId}
          autoComplete="off"
          autoFocus
          {...register('workspace')}
          placeholder={`${ui.placeholders.login.workspace}.${workspaceSuffix.replace(/^\./, '')}`}
          domain={workspaceSuffix}
          invalid={Boolean(errors.workspace)}
          aria-invalid={Boolean(errors.workspace)}
          aria-describedby={errors.workspace ? `${workspaceId}-error` : undefined}
          disabled={isSubmitting}
        />
        {errors.workspace?.message && (
          <ErrorMessage id={`${workspaceId}-error`}>{errors.workspace.message}</ErrorMessage>
        )}
      </Field>

      <button
        type="button"
        onClick={onForgotPassword}
        className="-mt-2 block cursor-pointer text-sm font-semibold text-accent-deep dark:text-accent-bright"
      >
        {copy.form.forgotPassword}
      </button>

      <ReCaptcha
        locale={locale}
        action="LOGIN"
        onChange={(token) => {
          setCaptchaToken(token)
          if (token) {
            setCaptchaError(null)
          }
        }}
        error={captchaError}
        resetKey={captchaResetKey}
        unavailableLabel={ui.validation.captchaUnavailable}
      />

      <div className="pt-2">
        <Button type="submit" size="lg" color="dark/zinc" disabled={isSubmitting}>
          {copy.form.submitLabel} <ArrowRightIcon className="size-4" />
        </Button>
      </div>

      <div className="flex items-center gap-3" aria-hidden="true">
        <span className="h-px flex-1 bg-mist-200 dark:bg-white/10" />
        <span className="text-xs text-mist-500 dark:text-mist-400">{copy.form.orDivider}</span>
        <span className="h-px flex-1 bg-mist-200 dark:bg-white/10" />
      </div>

      <p className="text-center text-sm/7 text-mist-700 dark:text-mist-300">
        {copy.form.footerPrompt}{' '}
        <Link href={signupHref} className="justify-center font-semibold text-accent-deep dark:text-accent-bright">
          {ui.actions.register} <ArrowRightIcon className="size-4" />
        </Link>
      </p>
    </form>
  )
}
